import {
  Project,
  ProjectPhase,
  ProjectTask,
  ProjectMilestone,
  ProjectResource,
  ProjectDependency,
  ProjectDeliverable,
  ProjectRisk,
  ProjectTimeline,
  ProjectBudget,
  ProjectTemplate,
  TemplatePhase,
  TemplateTask,
  TemplateResource,
  TemplateRisk,
} from '@/types/services/implementation/project';

export class ProjectPlanningService {
  // =====================================
  // PROJECT CREATION & MANAGEMENT
  // =====================================

  /**
   * Create a new implementation project
   */
  async createProject(
    customerId: string,
    contractId: string,
    assessmentId: string,
    projectData: Partial<Project>,
  ): Promise<Project> {
    const project: Project = {
      id: `project_${Date.now()}`,
      customerId,
      contractId,
      assessmentId,
      name: projectData.name || 'Community App Implementation',
      description: projectData.description || 'Implementation of community management platform',
      type: 'implementation',
      status: 'planning',
      priority: 'high',
      phases: [],
      timeline: this.createEmptyTimeline(),
      budget: this.createEmptyBudget(),
      resources: [],
      dependencies: [],
      deliverables: [],
      risks: [],
      milestones: [],
      settings: {
        allowParallelExecution: true,
        autoScheduling: true,
        criticalPathTracking: true,
        riskThreshold: 'medium',
        reportingFrequency: 'weekly',
        approvalRequired: true,
      },
      metadata: {
        industry: 'Community Management',
        complexity: 'medium',
        estimatedDuration: 12,
        teamSize: 5,
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'current_user', // In real app, this would be the current user
    };

    this.saveProject(project);
    return project;
  }

  /**
   * Generate project template based on assessment results
   */
  generateProjectTemplate(assessmentId: string): ProjectTemplate {
    // This would analyze the technical assessment and generate an appropriate template
    return {
      id: 'standard_implementation',
      name: 'Standard Community App Implementation',
      description: 'Standard template for community management platform implementation',
      category: 'implementation',
      estimatedDuration: 12,
      complexity: 'medium',
      phases: [
        {
          id: 'planning',
          name: 'Planning & Discovery',
          description: 'Project planning and requirements gathering',
          duration: 2,
          dependencies: [],
          tasks: [
            {
              id: 'kickoff',
              name: 'Project Kickoff Meeting',
              description: 'Initial project kickoff and team introduction',
              type: 'meeting',
              estimatedHours: 4,
              priority: 'high',
              dependencies: [],
            },
            {
              id: 'requirements_review',
              name: 'Requirements Review',
              description: 'Review and validate technical requirements',
              type: 'analysis',
              estimatedHours: 16,
              priority: 'high',
              dependencies: ['kickoff'],
            },
            {
              id: 'project_plan',
              name: 'Detailed Project Plan',
              description: 'Create detailed project plan and timeline',
              type: 'planning',
              estimatedHours: 12,
              priority: 'high',
              dependencies: ['requirements_review'],
            },
          ],
        },
        {
          id: 'infrastructure',
          name: 'Infrastructure Setup',
          description: 'Cloud infrastructure and environment setup',
          duration: 3,
          dependencies: ['planning'],
          tasks: [
            {
              id: 'cloud_setup',
              name: 'Cloud Environment Setup',
              description: 'Provision cloud infrastructure and services',
              type: 'infrastructure',
              estimatedHours: 24,
              priority: 'high',
              dependencies: [],
            },
            {
              id: 'security_config',
              name: 'Security Configuration',
              description: 'Configure security settings and access controls',
              type: 'security',
              estimatedHours: 16,
              priority: 'high',
              dependencies: ['cloud_setup'],
            },
            {
              id: 'network_setup',
              name: 'Network Configuration',
              description: 'Configure network settings and connectivity',
              type: 'infrastructure',
              estimatedHours: 8,
              priority: 'medium',
              dependencies: ['cloud_setup'],
            },
          ],
        },
        {
          id: 'data_migration',
          name: 'Data Migration',
          description: 'Migrate existing data to new system',
          duration: 4,
          dependencies: ['infrastructure'],
          tasks: [
            {
              id: 'data_analysis',
              name: 'Data Analysis & Mapping',
              description: 'Analyze existing data and create migration mappings',
              type: 'analysis',
              estimatedHours: 32,
              priority: 'high',
              dependencies: [],
            },
            {
              id: 'data_cleanup',
              name: 'Data Cleanup',
              description: 'Clean and prepare data for migration',
              type: 'data_processing',
              estimatedHours: 40,
              priority: 'high',
              dependencies: ['data_analysis'],
            },
            {
              id: 'migration_scripts',
              name: 'Migration Scripts Development',
              description: 'Develop and test data migration scripts',
              type: 'development',
              estimatedHours: 24,
              priority: 'high',
              dependencies: ['data_cleanup'],
            },
            {
              id: 'data_migration',
              name: 'Data Migration Execution',
              description: 'Execute data migration and validation',
              type: 'migration',
              estimatedHours: 16,
              priority: 'critical',
              dependencies: ['migration_scripts'],
            },
          ],
        },
        {
          id: 'system_configuration',
          name: 'System Configuration',
          description: 'Configure and customize the application',
          duration: 3,
          dependencies: ['data_migration'],
          tasks: [
            {
              id: 'app_config',
              name: 'Application Configuration',
              description: 'Configure application settings and features',
              type: 'configuration',
              estimatedHours: 20,
              priority: 'high',
              dependencies: [],
            },
            {
              id: 'integrations',
              name: 'System Integrations',
              description: 'Set up integrations with external systems',
              type: 'integration',
              estimatedHours: 32,
              priority: 'high',
              dependencies: ['app_config'],
            },
            {
              id: 'customizations',
              name: 'Custom Features',
              description: 'Implement custom features and workflows',
              type: 'development',
              estimatedHours: 24,
              priority: 'medium',
              dependencies: ['app_config'],
            },
          ],
        },
        {
          id: 'testing',
          name: 'Testing & Quality Assurance',
          description: 'Comprehensive testing and quality assurance',
          duration: 2,
          dependencies: ['system_configuration'],
          tasks: [
            {
              id: 'system_testing',
              name: 'System Testing',
              description: 'Comprehensive system and integration testing',
              type: 'testing',
              estimatedHours: 32,
              priority: 'high',
              dependencies: [],
            },
            {
              id: 'uat',
              name: 'User Acceptance Testing',
              description: 'User acceptance testing with stakeholders',
              type: 'testing',
              estimatedHours: 16,
              priority: 'high',
              dependencies: ['system_testing'],
            },
            {
              id: 'performance_testing',
              name: 'Performance Testing',
              description: 'Performance and load testing',
              type: 'testing',
              estimatedHours: 12,
              priority: 'medium',
              dependencies: ['system_testing'],
            },
          ],
        },
        {
          id: 'training',
          name: 'Training & Documentation',
          description: 'User training and documentation',
          duration: 2,
          dependencies: ['testing'],
          tasks: [
            {
              id: 'documentation',
              name: 'User Documentation',
              description: 'Create user manuals and documentation',
              type: 'documentation',
              estimatedHours: 20,
              priority: 'high',
              dependencies: [],
            },
            {
              id: 'training_materials',
              name: 'Training Materials',
              description: 'Develop training materials and guides',
              type: 'training',
              estimatedHours: 16,
              priority: 'high',
              dependencies: ['documentation'],
            },
            {
              id: 'user_training',
              name: 'User Training Sessions',
              description: 'Conduct user training sessions',
              type: 'training',
              estimatedHours: 12,
              priority: 'high',
              dependencies: ['training_materials'],
            },
          ],
        },
        {
          id: 'deployment',
          name: 'Deployment & Go-Live',
          description: 'Production deployment and go-live support',
          duration: 1,
          dependencies: ['training'],
          tasks: [
            {
              id: 'production_deployment',
              name: 'Production Deployment',
              description: 'Deploy system to production environment',
              type: 'deployment',
              estimatedHours: 8,
              priority: 'critical',
              dependencies: [],
            },
            {
              id: 'go_live',
              name: 'Go-Live Support',
              description: 'Provide go-live support and monitoring',
              type: 'support',
              estimatedHours: 16,
              priority: 'critical',
              dependencies: ['production_deployment'],
            },
          ],
        },
      ],
      resources: [
        {
          type: 'human',
          role: 'Project Manager',
          skillLevel: 'senior',
          allocation: 50,
          required: true,
        },
        {
          type: 'human',
          role: 'Technical Lead',
          skillLevel: 'expert',
          allocation: 75,
          required: true,
        },
        {
          type: 'human',
          role: 'Systems Administrator',
          skillLevel: 'senior',
          allocation: 100,
          required: true,
        },
        {
          type: 'human',
          role: 'Data Migration Specialist',
          skillLevel: 'senior',
          allocation: 100,
          required: true,
        },
        {
          type: 'human',
          role: 'Training Specialist',
          skillLevel: 'mid',
          allocation: 50,
          required: false,
        },
      ],
      risks: [
        {
          category: 'technical',
          description: 'Data migration complexity',
          impact: 'high',
          likelihood: 'medium',
          mitigation: 'Thorough data analysis and testing',
        },
        {
          category: 'resource',
          description: 'Resource availability',
          impact: 'medium',
          likelihood: 'low',
          mitigation: 'Early resource planning and allocation',
        },
      ],
      prerequisites: [
        'Technical assessment completed',
        'Contract signed',
        'Infrastructure access provided',
        'Stakeholder availability confirmed',
      ],
    };
  }

  /**
   * Create project from template
   */
  async createProjectFromTemplate(
    customerId: string,
    contractId: string,
    assessmentId: string,
    templateId: string,
    customizations?: Partial<Project>,
  ): Promise<Project> {
    const template = this.generateProjectTemplate(assessmentId);

    const project: Project = {
      id: `project_${Date.now()}`,
      customerId,
      contractId,
      assessmentId,
      name: customizations?.name || template.name,
      description: customizations?.description || template.description,
      type: 'implementation',
      status: 'planning',
      priority: 'high',
      phases: this.convertTemplatePhases(template.phases),
      timeline: this.generateTimeline(template.phases),
      budget: this.generateBudget(template.phases),
      resources: this.convertTemplateResources(template.resources),
      dependencies: [],
      deliverables: this.generateDeliverables(template.phases),
      risks: this.convertTemplateRisks(template.risks),
      milestones: this.generateMilestones(template.phases),
      settings: {
        allowParallelExecution: true,
        autoScheduling: true,
        criticalPathTracking: true,
        riskThreshold: 'medium',
        reportingFrequency: 'weekly',
        approvalRequired: true,
      },
      metadata: {
        industry: 'Community Management',
        complexity: template.complexity,
        estimatedDuration: template.estimatedDuration,
        teamSize: template.resources.length,
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      createdBy: 'current_user',
    };

    this.saveProject(project);
    return project;
  }

  // =====================================
  // PROJECT SCHEDULING & TIMELINE
  // =====================================

  /**
   * Calculate project timeline with dependencies
   */
  calculateProjectTimeline(projectId: string): ProjectTimeline {
    const project = this.getProject(projectId);
    if (!project) {
      throw new Error('Project not found');
    }

    const startDate = new Date();
    let currentDate = new Date(startDate);
    const phases: Array<{ phase: ProjectPhase; startDate: Date; endDate: Date }> = [];

    // Calculate each phase timeline
    project.phases.forEach((phase) => {
      const phaseStartDate = new Date(currentDate);
      const phaseEndDate = new Date(currentDate);
      phaseEndDate.setDate(phaseEndDate.getDate() + phase.duration * 7); // Convert weeks to days

      phases.push({
        phase,
        startDate: phaseStartDate,
        endDate: phaseEndDate,
      });

      // Update current date for next phase
      currentDate = new Date(phaseEndDate);
    });

    const endDate = phases[phases.length - 1]?.endDate || currentDate;

    const timeline: ProjectTimeline = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      duration: Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 7)), // weeks
      phases: phases.map((p) => ({
        phaseId: p.phase.id,
        startDate: p.startDate.toISOString(),
        endDate: p.endDate.toISOString(),
        duration: p.phase.duration,
        status: 'planned',
      })),
      criticalPath: this.calculateCriticalPath(project),
      milestoneSchedule: project.milestones.map((milestone) => ({
        milestoneId: milestone.id,
        scheduledDate: this.calculateMilestoneDate(milestone, phases),
        actualDate: milestone.completedAt,
      })),
    };

    // Update project with calculated timeline
    project.timeline = timeline;
    project.updatedAt = new Date().toISOString();
    this.saveProject(project);

    return timeline;
  }

  /**
   * Calculate critical path
   */
  private calculateCriticalPath(project: Project): string[] {
    // Simplified critical path calculation
    // In a real implementation, this would use more sophisticated algorithms
    const criticalTasks: string[] = [];

    project.phases.forEach((phase) => {
      const criticalPhaseTasks = phase.tasks
        .filter((task) => task.priority === 'critical' || task.priority === 'high')
        .map((task) => task.id);
      criticalTasks.push(...criticalPhaseTasks);
    });

    return criticalTasks;
  }

  /**
   * Calculate milestone date based on phase schedule
   */
  private calculateMilestoneDate(
    milestone: ProjectMilestone,
    phases: Array<{ phase: ProjectPhase; startDate: Date; endDate: Date }>,
  ): string {
    const relatedPhase = phases.find((p) =>
      p.phase.tasks.some((task) => task.id === milestone.dependsOn[0]),
    );

    if (relatedPhase) {
      return relatedPhase.endDate.toISOString();
    }

    return new Date().toISOString();
  }

  // =====================================
  // RESOURCE MANAGEMENT
  // =====================================

  /**
   * Calculate resource allocation and conflicts
   */
  calculateResourceAllocation(projectId: string): {
    totalCapacity: number;
    allocated: number;
    available: number;
    conflicts: Array<{
      resourceId: string;
      conflictType: 'overallocation' | 'unavailable';
      details: string;
    }>;
  } {
    const project = this.getProject(projectId);
    if (!project) {
      throw new Error('Project not found');
    }

    let totalCapacity = 0;
    let allocated = 0;
    const conflicts: Array<{
      resourceId: string;
      conflictType: 'overallocation' | 'unavailable';
      details: string;
    }> = [];

    project.resources.forEach((resource) => {
      if (resource.type === 'human') {
        totalCapacity += resource.allocation;
        allocated += resource.allocation;

        // Check for overallocation (simplified)
        if (resource.allocation > 100) {
          conflicts.push({
            resourceId: resource.id,
            conflictType: 'overallocation',
            details: `Resource allocated at ${resource.allocation}% capacity`,
          });
        }
      }
    });

    return {
      totalCapacity,
      allocated,
      available: totalCapacity - allocated,
      conflicts,
    };
  }

  /**
   * Optimize resource allocation
   */
  optimizeResourceAllocation(projectId: string): {
    optimizations: Array<{
      type: 'reallocation' | 'addition' | 'removal';
      resource: string;
      recommendation: string;
      impact: string;
    }>;
    estimatedSavings: number;
    riskReduction: string;
  } {
    const project = this.getProject(projectId);
    if (!project) {
      throw new Error('Project not found');
    }

    const optimizations = [];
    let estimatedSavings = 0;

    // Analyze resource utilization
    const underutilizedResources = project.resources.filter(
      (r) => r.type === 'human' && r.allocation < 50,
    );

    underutilizedResources.forEach((resource) => {
      optimizations.push({
        type: 'reallocation' as const,
        resource: resource.name,
        recommendation: `Increase allocation to 75% or reassign to other projects`,
        impact: 'Improved efficiency and cost effectiveness',
      });
      estimatedSavings += 5000; // Simplified calculation
    });

    // Check for missing critical resources
    const hasProjectManager = project.resources.some((r) => r.role === 'Project Manager');
    if (!hasProjectManager) {
      optimizations.push({
        type: 'addition' as const,
        resource: 'Project Manager',
        recommendation: 'Add dedicated project manager for better coordination',
        impact: 'Reduced delivery risk and improved timeline adherence',
      });
    }

    return {
      optimizations,
      estimatedSavings,
      riskReduction: 'Medium - optimized resource allocation reduces delivery risks',
    };
  }

  // =====================================
  // RISK MANAGEMENT
  // =====================================

  /**
   * Assess project risks
   */
  assessProjectRisks(projectId: string): {
    overallRiskScore: number;
    riskLevel: 'low' | 'medium' | 'high' | 'critical';
    topRisks: ProjectRisk[];
    mitigationPlan: string;
  } {
    const project = this.getProject(projectId);
    if (!project) {
      throw new Error('Project not found');
    }

    let totalRiskScore = 0;
    project.risks.forEach((risk) => {
      const impactScore = this.getImpactScore(risk.impact);
      const likelihoodScore = this.getLikelihoodScore(risk.likelihood);
      risk.riskScore = impactScore * likelihoodScore;
      totalRiskScore += risk.riskScore;
    });

    const averageRiskScore = project.risks.length > 0 ? totalRiskScore / project.risks.length : 0;
    const topRisks = project.risks
      .sort((a, b) => (b.riskScore || 0) - (a.riskScore || 0))
      .slice(0, 5);

    let riskLevel: 'low' | 'medium' | 'high' | 'critical';
    if (averageRiskScore >= 16) riskLevel = 'critical';
    else if (averageRiskScore >= 12) riskLevel = 'high';
    else if (averageRiskScore >= 8) riskLevel = 'medium';
    else riskLevel = 'low';

    return {
      overallRiskScore: averageRiskScore,
      riskLevel,
      topRisks,
      mitigationPlan:
        'Implement proactive monitoring and risk mitigation strategies for identified high-risk areas.',
    };
  }

  /**
   * Generate risk mitigation plan
   */
  generateRiskMitigationPlan(projectId: string): {
    strategies: Array<{
      risk: string;
      strategy: string;
      timeline: string;
      responsible: string;
      cost: number;
    }>;
    monitoringPlan: string;
    contingencyPlan: string;
  } {
    const project = this.getProject(projectId);
    if (!project) {
      throw new Error('Project not found');
    }

    const strategies = project.risks.map((risk) => ({
      risk: risk.description,
      strategy: risk.mitigation,
      timeline: '1-2 weeks',
      responsible: 'Project Manager',
      cost: this.estimateRiskMitigationCost(risk),
    }));

    return {
      strategies,
      monitoringPlan: 'Weekly risk review meetings with automated risk indicator monitoring',
      contingencyPlan:
        'Maintain 10% budget contingency and backup resource allocation for high-priority risks',
    };
  }

  // =====================================
  // HELPER METHODS
  // =====================================

  private convertTemplatePhases(templatePhases: TemplatePhase[]): ProjectPhase[] {
    return templatePhases.map((phase) => ({
      id: phase.id,
      name: phase.name,
      description: phase.description,
      status: 'planned',
      startDate: '',
      endDate: '',
      duration: phase.duration,
      progress: 0,
      dependencies: phase.dependencies,
      tasks: phase.tasks.map((task: TemplateTask) => ({
        id: task.id,
        name: task.name,
        description: task.description,
        type: task.type,
        status: 'planned',
        priority: task.priority,
        estimatedHours: task.estimatedHours,
        actualHours: 0,
        progress: 0,
        assignedTo: '',
        dependencies: task.dependencies,
        startDate: '',
        endDate: '',
        deliverables: [],
        comments: [],
        attachments: [],
      })),
    }));
  }

  private convertTemplateResources(templateResources: TemplateResource[]): ProjectResource[] {
    return templateResources.map((resource, index) => ({
      id: `resource_${index}`,
      type: resource.type,
      name: resource.role,
      role: resource.role,
      skillLevel: resource.skillLevel,
      allocation: resource.allocation,
      availability: [],
      cost: this.estimateResourceCost(resource),
      required: resource.required,
    }));
  }

  private convertTemplateRisks(templateRisks: TemplateRisk[]): ProjectRisk[] {
    return templateRisks.map((risk, index) => ({
      id: `risk_${index}`,
      category: risk.category,
      description: risk.description,
      impact: risk.impact,
      likelihood: risk.likelihood,
      riskScore: this.getImpactScore(risk.impact) * this.getLikelihoodScore(risk.likelihood),
      mitigation: risk.mitigation,
      owner: 'Project Manager',
      status: 'identified',
    }));
  }

  private generateTimeline(templatePhases: TemplatePhase[]): ProjectTimeline {
    const totalDuration = templatePhases.reduce((sum, phase) => sum + phase.duration, 0);
    const startDate = new Date();
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + totalDuration * 7);

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      duration: totalDuration,
      phases: [],
      criticalPath: [],
      milestoneSchedule: [],
    };
  }

  private generateBudget(templatePhases: TemplatePhase[]): ProjectBudget {
    const totalHours = templatePhases.reduce(
      (sum, phase) =>
        sum +
        phase.tasks.reduce(
          (taskSum: number, task: TemplateTask) => taskSum + task.estimatedHours,
          0,
        ),
      0,
    );

    const laborCost = totalHours * 150; // $150/hour average
    const infrastructureCost = 10000;
    const softwareCost = 5000;
    const contingency = (laborCost + infrastructureCost + softwareCost) * 0.1;

    return {
      totalBudget: laborCost + infrastructureCost + softwareCost + contingency,
      allocated: 0,
      spent: 0,
      remaining: laborCost + infrastructureCost + softwareCost + contingency,
      categories: [
        {
          category: 'Labor',
          budgeted: laborCost,
          allocated: 0,
          spent: 0,
          remaining: laborCost,
        },
        {
          category: 'Infrastructure',
          budgeted: infrastructureCost,
          allocated: 0,
          spent: 0,
          remaining: infrastructureCost,
        },
        {
          category: 'Software',
          budgeted: softwareCost,
          allocated: 0,
          spent: 0,
          remaining: softwareCost,
        },
        {
          category: 'Contingency',
          budgeted: contingency,
          allocated: 0,
          spent: 0,
          remaining: contingency,
        },
      ],
      forecastAccuracy: 85,
    };
  }

  private generateDeliverables(templatePhases: TemplatePhase[]): ProjectDeliverable[] {
    const deliverables: ProjectDeliverable[] = [];

    templatePhases.forEach((phase, phaseIndex) => {
      deliverables.push({
        id: `deliverable_${phaseIndex}`,
        name: `${phase.name} Completion`,
        description: `All tasks and outputs for ${phase.name} phase`,
        type: 'phase_completion',
        phaseId: phase.id,
        dueDate: '',
        status: 'planned',
        approver: 'Customer',
        acceptanceCriteria: [`All ${phase.name} tasks completed successfully`],
        attachments: [],
      });
    });

    return deliverables;
  }

  private generateMilestones(templatePhases: TemplatePhase[]): ProjectMilestone[] {
    return templatePhases.map((phase, index) => ({
      id: `milestone_${index}`,
      name: `${phase.name} Complete`,
      description: `Completion of ${phase.name} phase`,
      type: 'phase_completion',
      dueDate: '',
      status: 'planned',
      dependsOn: [phase.id],
      approver: 'Customer',
      acceptanceCriteria: [`All ${phase.name} deliverables approved`],
    }));
  }

  private estimateResourceCost(resource: TemplateResource): number {
    const hourlyRates: Record<string, number> = {
      'Project Manager': 150,
      'Technical Lead': 175,
      'Systems Administrator': 125,
      'Data Migration Specialist': 140,
      'Training Specialist': 100,
    };

    return hourlyRates[resource.role] || 125;
  }

  private estimateRiskMitigationCost(risk: ProjectRisk): number {
    const baseCosts: Record<string, number> = {
      technical: 5000,
      resource: 3000,
      schedule: 2000,
      budget: 1000,
      quality: 4000,
    };

    return baseCosts[risk.category] || 2500;
  }

  private getImpactScore(impact: string): number {
    const scores = { negligible: 1, low: 2, medium: 3, high: 4, critical: 5 };
    return scores[impact as keyof typeof scores] || 3;
  }

  private getLikelihoodScore(likelihood: string): number {
    const scores = { very_low: 1, low: 2, medium: 3, high: 4, very_high: 5 };
    return scores[likelihood as keyof typeof scores] || 3;
  }

  private createEmptyTimeline(): ProjectTimeline {
    return {
      startDate: '',
      endDate: '',
      duration: 0,
      phases: [],
      criticalPath: [],
      milestoneSchedule: [],
    };
  }

  private createEmptyBudget(): ProjectBudget {
    return {
      totalBudget: 0,
      allocated: 0,
      spent: 0,
      remaining: 0,
      categories: [],
      forecastAccuracy: 0,
    };
  }

  // =====================================
  // STORAGE METHODS (Mock Implementation)
  // =====================================

  private saveProject(project: Project): void {
    localStorage.setItem(`project_${project.id}`, JSON.stringify(project));
  }

  private getProject(id: string): Project | null {
    const data = localStorage.getItem(`project_${id}`);
    return data ? JSON.parse(data) : null;
  }

  public getProjectsByCustomer(customerId: string): Project[] {
    // Mock implementation - in real app, this would query database
    return [];
  }

  public updateProjectStatus(id: string, status: Project['status']): void {
    const project = this.getProject(id);
    if (project) {
      project.status = status;
      project.updatedAt = new Date().toISOString();
      this.saveProject(project);
    }
  }

  public updateTaskProgress(projectId: string, taskId: string, progress: number): void {
    const project = this.getProject(projectId);
    if (project) {
      project.phases.forEach((phase) => {
        const task = phase.tasks.find((t) => t.id === taskId);
        if (task) {
          task.progress = progress;
          if (progress >= 100) {
            task.status = 'completed';
          } else if (progress > 0) {
            task.status = 'in_progress';
          }
        }
      });
      project.updatedAt = new Date().toISOString();
      this.saveProject(project);
    }
  }
}

// Export singleton instance
export const projectPlanningService = new ProjectPlanningService();
